import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useI18n } from './I18nContext';
import { getBasename, getAuthCallbackUrl } from '@/utils/url';

// Custom hook for navigation
const useNavigation = () => {
  const navigate = (path: string) => {
    window.location.href = path;
  };
  return navigate;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
    });

    // Initial session check
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (!session) {
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session.user);
        setLoading(false);
      } catch (error) {
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const redirectUrl = getAuthCallbackUrl();
    
      // First, create the user in authentication
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: userData
        }
      });
    
      if (error) {
        console.error('Error signing up:', error);
        if (error.message.includes('already registered') || error.message.includes('violates foreign key constraint')) {
          toast({
            title: t('auth.email_already_registered'),
            description: t('auth.email_already_registered_description'),
            variant: "destructive"
          });
          // Redirect to sign in page
          navigate('/?tab=signin');
        } else {
          toast({
            title: t('auth.sign_up_error'),
            description: error.message,
            variant: "destructive"
          });
        }
        return { error };
      }

      if (!data.user) {
        console.error('No user data returned');
        toast({
          title: t('auth.sign_up_error'),
          description: t('auth.no_user_data_returned'),
          variant: "destructive"
        });
        return { error: new Error(t('auth.no_user_data_returned')) };
      }

      // Wait for the user to be created in the database
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Now create the profile
      const { error: profileError } = await supabase.rpc('create_profile', {
        user_id: data.user.id,
        user_metadata: userData
      });

      if (profileError) {
        console.error('Error creating profile:', profileError);
        // Show friendly error message instead of raw database error
        toast({
          title: t('auth.sign_up_error'),
          description: t('auth.failed_to_create_account'),
          variant: "destructive"
        });
        // Return the original error so the UI can inspect code/details
        return { error: profileError };
      }

      // Sign out immediately after sign up to ensure email confirmation is required
      await supabase.auth.signOut();
      
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signup:', error);
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
    return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
    
      if (error) {
        console.log('Supabase sign in error:', error);
        
        // Handle specific error cases
        if (error.message?.toLowerCase().includes('email not confirmed') || 
            error.code === 'email_not_confirmed') {
          toast({
            title: t('auth.email_not_confirmed'),
            description: t('auth.email_not_confirmed_description'),
            variant: "destructive"
          });
        } else if (error.message?.toLowerCase().includes('invalid login credentials') ||
                   error.message?.toLowerCase().includes('invalid email or password')) {
          toast({
            title: t('auth.sign_in_failed'),
            description: t('auth.sign_in_failed_description'),
            variant: "destructive"
          });
        } else if (error.message?.toLowerCase().includes('too many requests')) {
          toast({
            title: t('auth.error'),
            description: t('auth.too_many_requests'),
            variant: "destructive"
          });
        } else {
          // For any other specific errors, show a generic but informative message
          toast({
            title: t('auth.sign_in_failed'),
            description: error.message || t('auth.sign_in_failed_description'),
            variant: "destructive"
          });
        }
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signin:', error);
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      
      // Clear wizard session storage
      sessionStorage.removeItem('showWizard');
      sessionStorage.removeItem('wizardShownThisSession');
      
      // Clear all Supabase session data from localStorage
      localStorage.removeItem('sb-kskwramebgqbbmahvloe-auth-token');
      
      // Then try to sign out from Supabase
      await supabase.auth.signOut();
      
      // Force reload the page to clear any remaining state
      window.location.href = getBasename();
    } catch (error) {
      console.error('Error in signOut:', error);
      // Even if there's an error, force reload to clear everything
      window.location.href = getBasename();
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

